/* =====================================================================
   AstaLab 2.0 — createInitialAuctionState() (round alpha.2-r11)

   Root cause del bug operativo "nuova asta = dead end" (vedi ROOT_CAUSE.md): createAuction()
   crea sempre e solo la riga `auctions`/`auction_states` non esiste ancora finché nessuno chiama
   saveAuction() — comportamento intenzionale del repository (vedi AuctionRepository.js,
   "un'asta senza stato salvato restituisce revision:0/state:null"), MAI però collegato a un'azione
   della UI per una nuova asta nativa (l'unico chiamante esistente di questo pattern era
   importLegacyBackup.js, per le aste importate da un backup 1.3). Questo modulo fornisce
   ESATTAMENTE quell'azione mancante: costruisce il primo stato valido, mai un ramo diverso che
   duplica la logica di persistenza (che resta AuctionRepository.saveAuction(), invariata).

   Perché una schermata di configurazione esplicita (soluzione B) e non un'inizializzazione
   automatica silenziosa (soluzione A): il motore esistente (astalabEngine.js, MAI modificato qui)
   richiede `config.partecipanti` e `config.budget` per calcolare scarsità/prezzi/inflazione — due
   valori che cambiano il risultato economico dell'intera asta e variano legittimamente da lega a
   lega. Inventare un default silenzioso per questi due campi specifici produrrebbe uno stato
   "tecnicamente valido" ma sostanzialmente sbagliato per la lega dell'utente. Ogni ALTRO campo del
   motore ha già un default sensato e già esistente nel progetto (DEFAULT_WEIGHTS,
   DEFAULT_TIER_THRESHOLDS, BUDGET_PRESETS, tutti importati qui invariati da astalabEngine.js, mai
   duplicati) — questo modulo li riusa così come sono, mai un nuovo schema.

   Composizione slot per ruolo (POR/DIF/CEN/ATT): a differenza di partecipanti/budget, la
   composizione standard di una rosa Fantacalcio (3 portieri, 8 difensori, 8 centrocampisti,
   6 attaccanti = 25 titolari) è una convenzione pressoché universale del gioco stesso, non una
   scelta economica specifica della lega — riusata qui come default ragionevole, regolabile in
   futuro quando l'editor di configurazione completo (fuori scopo di questo round, vedi
   AuctionDetailPage.js) sarà disponibile.
   ===================================================================== */
import { DEFAULT_WEIGHTS, DEFAULT_TIER_THRESHOLDS, BUDGET_PRESETS } from "./astalabEngine.js";

export const DEFAULT_BUDGET_PRESET_KEY = "equilibrato";
const DEFAULT_ROLE_SLOTS = { slotPOR: 3, slotDIF: 8, slotCEN: 8, slotATT: 6 };
const DEFAULT_PREZZO_MINIMO = 1;
const MIN_PARTECIPANTI = 2, MAX_PARTECIPANTI = 20;
const MIN_BUDGET = 50, MAX_BUDGET = 5000;

/** Valida SOLO i due campi che richiedono davvero l'input dell'utente (mai un default fabbricato
 *  per questi) — funzione pura, usata sia dalla UI (validazione prima dell'invio) sia da
 *  createInitialAuctionState() stessa (mai una doppia logica divergente). Ritorna un array di
 *  messaggi d'errore leggibili (vuoto se tutto valido). */
export function validateInitialAuctionSetup({ partecipanti, budget }){
  const errors = [];
  const p = Number(partecipanti);
  const b = Number(budget);
  if(partecipanti === "" || partecipanti === null || partecipanti === undefined || !Number.isInteger(p) || p < MIN_PARTECIPANTI || p > MAX_PARTECIPANTI){
    errors.push("Il numero di partecipanti deve essere un numero intero fra " + MIN_PARTECIPANTI + " e " + MAX_PARTECIPANTI + ".");
  }
  if(budget === "" || budget === null || budget === undefined || !Number.isInteger(b) || b < MIN_BUDGET || b > MAX_BUDGET){
    errors.push("Il budget per squadra deve essere un numero intero fra " + MIN_BUDGET + " e " + MAX_BUDGET + " crediti.");
  }
  return errors;
}

/** Costruisce il primo stato valido di una nuova asta AstaLab 2.0 nativa (mai un'asta importata da
 *  backup 1.3, che ha il proprio involucro schemaOrigin="astalab-1.3" — vedi
 *  importLegacyBackup.js). Lancia un errore (mai un fabbricato "successo parziale") se
 *  partecipanti/budget non sono validi: il chiamante deve validare PRIMA con
 *  validateInitialAuctionSetup() per mostrare un errore comprensibile senza affidarsi
 *  all'eccezione come unico controllo. */
export function createInitialAuctionState({ partecipanti, budget, budgetPresetKey, slotPOR, slotDIF, slotCEN, slotATT }){
  const errors = validateInitialAuctionSetup({ partecipanti, budget });
  if(errors.length) throw new Error(errors.join(" "));
  const presetKey = Object.prototype.hasOwnProperty.call(BUDGET_PRESETS, budgetPresetKey) ? budgetPresetKey : DEFAULT_BUDGET_PRESET_KEY;
  // Round 2.1.0: taglie di rosa per ruolo opzionali — un chiamante classico che le omette (come
  // l'unico chiamante attuale, AuctionDetailPage.js) ottiene esattamente DEFAULT_ROLE_SLOTS
  // (3/8/8/6), comportamento invariato. `?? ` e non `||`: uno 0 esplicito per un ruolo è un valore
  // legittimo (lega che non usa quel ruolo), mai silenziosamente sostituito dal default.
  const roleSlots = {
    slotPOR: slotPOR ?? DEFAULT_ROLE_SLOTS.slotPOR, slotDIF: slotDIF ?? DEFAULT_ROLE_SLOTS.slotDIF,
    slotCEN: slotCEN ?? DEFAULT_ROLE_SLOTS.slotCEN, slotATT: slotATT ?? DEFAULT_ROLE_SLOTS.slotATT
  };

  return {
    // Involucro esplicito, stesso principio di importLegacyBackup.js (schemaOrigin/raw) — mai lo
    // stato "nudo" salvato direttamente, cosi' AuctionDetailPage.js può sempre distinguere in modo
    // affidabile un'asta nativa 2.0 da una importata, senza euristiche sulla FORMA del contenuto.
    schemaOrigin: "astalab-2.0",
    createdAt: new Date().toISOString(),
    raw: {
      players: [],
      teams: [],
      purchaseLedger: [],
      auctionEpoch: 0,
      config: Object.assign(
        { partecipanti: Number(partecipanti), budget: Number(budget), prezzoMinimo: DEFAULT_PREZZO_MINIMO, modDifesaAttivo: false },
        roleSlots
      ),
      weights: Object.assign({}, DEFAULT_WEIGHTS),
      tierThresholds: Object.assign({}, DEFAULT_TIER_THRESHOLDS),
      budgetSplit: Object.assign({}, BUDGET_PRESETS[presetKey]),
      budgetPresetKey: presetKey
    }
  };
}
